import React from 'react';
import { AccordionDetails, Box, Stack, TextField, Typography, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { GameLessonProgress, LessonProgress, VrLessonProgress } from 'models/LessonProgress';
import { LessonProgressStatus } from 'models/LessonProgressStatus';
import { useGetLessonProgressNodes } from 'hooks/useGetLessonProgressNodes';
import { VrLessonProgressSection } from 'components/views/myTrainings/VrLessonProgressSection/VrLessonProgressSection';
import { GameLessonProgressSummary } from 'components/views/myTrainings/GameLessonProgressSummary/GameLessonProgressSummary';
import HistoricalProgresses from 'components/views/myTrainings/HistoricalProgresses/HistoricalProgresses';
import {
  HistoricalAccordion,
  HistoricalAccordionSummary,
} from 'components/views/myTrainings/LessonProgressAccordion/LessonProgressAccordion.styles';

interface LessonProgressDetailsProps {
  lp: LessonProgress;
  historicalProgresses: LessonProgress[];
  isVr: boolean;
  isGame: boolean;
}

export const LessonProgressDetails = (props: LessonProgressDetailsProps) => {
  const { lp, historicalProgresses, isVr, isGame } = props;
  const theme = useTheme();
  const { t } = useTranslation();
  const { nodes, feedback, gettingNodes } = useGetLessonProgressNodes(lp.id);

  const isCompleted = lp.lessonProgressStatus === LessonProgressStatus.Finished;

  return (
    <AccordionDetails>
      <Stack spacing={theme.spacing(2)}>
        {isVr && (
          <VrLessonProgressSection
            lp={lp as VrLessonProgress}
            isCompleted={isCompleted}
            nodes={nodes}
            gettingNodes={gettingNodes}
          />
        )}
        {isGame && (
          <GameLessonProgressSummary
            lp={lp as GameLessonProgress}
            isCompleted={isCompleted}
          />
        )}
        {feedback && (
          <TextField
            label={t('myTrainings.assignedTrainingFeedback')}
            value={feedback}
            fullWidth
            multiline
            minRows={1.1}
            InputProps={{ readOnly: true }}
            variant="outlined"
            sx={{
              '& .MuiInputBase-multiline': { display: 'flex', alignItems: 'center' },
              '& .MuiInputBase-inputMultiline': { textAlign: 'left' },
            }}
          />
        )}
        <Box padding={theme.spacing(2)}>
          <HistoricalAccordion elevation={0} disableGutters>
            <HistoricalAccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="historical-progresses-content"
              id="historical-progresses-header"
            >
              <Typography>
                {`${t('myTrainings.historicalProgress.archival')} (${historicalProgresses.length})`}
              </Typography>
            </HistoricalAccordionSummary>
            <HistoricalProgresses historicalProgresses={historicalProgresses} />
          </HistoricalAccordion>
        </Box>
      </Stack>
    </AccordionDetails>
  );
};
